import {
  ClockCircleFilled,
  CloseOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { SelectedContact } from '@mm/state/interfaces/contacts';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import { BusinessAttributes } from '../../../liveChat/components/Contact/components/BusinessAttributes';
import { LinkedContacts } from '../../../liveChat/components/Contact/components/LinkedContacts';
import Notes from '../../../liveChat/components/Contact/components/Notes';
import { OTNTags } from '../../../liveChat/components/Contact/components/OTNTags';
import Sessions from '../../../liveChat/components/Contact/components/Sessions';
import { ContactDeleteModal } from '../ContactDeleteModal/ContactDeleteModal';
import './contactDetails.scss';
import ContactsDetailsHeader from './contactDetailsHeader/contactDetailsHeader';
import ChatVariablesTab from './contactDetailsTabs/chatVariablesTab/chatVariablesTab';
import { ChatVariablesTags } from './contactDetailsTabs/chatVariablesTags/chatVariablesTags';
import { CustomerJourney } from './contactDetailsTabs/customerJourney/customerJourney';
import { LeadScoring } from './contactDetailsTabs/leadScoring/leadScoring';

type OuterProps = {
  selectedContact: SelectedContact;
  closeDetails: () => void;
  onDeleteSelectedContact: (
    id: number,
    deleteRemoteMessages: boolean,
  ) => Promise<void>;
  onContactSelect?: (id: number) => void;
  refreshSelectedContact: () => void;
};

export const BUSINESS_EMAIL = 'VOY_BUSINESS_EMAIL';
export const BUSINESS_PHONE = 'VOY_COMPANY_PHONE';

const ContactsDetails = (props: OuterProps) => {
  const {
    selectedContact,
    closeDetails,
    onDeleteSelectedContact,
    refreshSelectedContact,
    onContactSelect = () => {},
  } = props;

  const displayVariables = selectedContact.variables?.filter(
    v => v.variable.name !== 'fb messenger user id',
  );

  const isBusinessContact = !!displayVariables.find(
    ({ variable }) => variable.name.toLocaleLowerCase() === 'business_contact',
  );

  const otnTags = selectedContact.tags.filter(tag => tag.kind === 'otn');

  const onContactClick = (contact: SelectedContact) =>
    onContactSelect(contact.id);

  const chatbotId = useSelector(activeChatbotIdSelector);
  const router = useRouter();

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  return (
    <div className="contact-details">
      {closeDetails && (
        <CloseOutlined
          className="contact-details__close-button"
          onClick={closeDetails}
        />
      )}
      <ContactsDetailsHeader
        deleteSelectedContact={() => setShowDeleteModal(true)}
        selectedContact={selectedContact}
        onChangeContactInfo={refreshSelectedContact}
        isBusinessContact={isBusinessContact}
      />
      <div className="contact-details__blocks">
        <LeadScoring
          leadScoring={selectedContact.leadScoring}
          openOnMount={selectedContact.dummy}
        />
        <CustomerJourney customerJourney={selectedContact.customerJourney} />
        <LinkedContacts
          linkedContacts={selectedContact.linkedContacts}
          onContactClick={onContactClick}
        />
        <ChatVariablesTags
          contactId={selectedContact.id}
          selectedContact={selectedContact}
          tags={selectedContact.tags}
          onChange={refreshSelectedContact}
        />
        <Sessions
          contact={selectedContact}
          onSessionClick={(session, contact, setSessionIdCallback) => {
            router.history.push(
              BotBuilderRoutes.liveChat.getLink({
                chatbotId,
                remoteUserId: contact.remoteId,
              }),
            );
            setSessionIdCallback(closeDetails);
          }}
        />
        <Notes
          contactId={selectedContact.id}
          selectedContact={selectedContact}
          notes={selectedContact.notes}
          onChange={refreshSelectedContact}
        />
        <OTNTags contactId={selectedContact.id} otnTags={otnTags} />
        <ChatVariablesTab
          contactId={selectedContact.id}
          selectedContact={selectedContact}
          variables={displayVariables}
          onAttributeUpdated={refreshSelectedContact}
          remoteUserId={selectedContact.remoteId}
          isBusinessContact={isBusinessContact}
        />
        <BusinessAttributes
          variables={displayVariables}
          isBusinessContact={isBusinessContact}
        />
        <div className="contact-details__footer-info">
          {selectedContact.recipientId && (
            <div className="contact-details__footer-info-row">
              <UserOutlined style={{ color: '#a5bbd4' }} />
              <div>id: {selectedContact.recipientId}</div>
            </div>
          )}
          <div className="contact-details__footer-info-row">
            <ClockCircleFilled style={{ color: '#a5bbd4' }} />
            <div>
              Last seen:{' '}
              {moment(selectedContact.lastActive).format('YYYY-MM-DD HH:mm')}
            </div>
          </div>
        </div>
      </div>
      <ContactDeleteModal
        showEraseRemoteMessagesOption={selectedContact.remoteId.startsWith(
          'em_',
        )}
        show={showDeleteModal}
        emailAddress={selectedContact.email}
        onClose={() => setShowDeleteModal(false)}
        onDelete={eraseRemoteMessages =>
          onDeleteSelectedContact(selectedContact.id, eraseRemoteMessages)
        }
      />
    </div>
  );
};

export default ContactsDetails;
